import React, { useState } from 'react';
import { graphql } from 'react-relay';

import { brokerLandingPageQueryResponse } from '@zego-types/brokerLandingPageQuery.graphql';
import BaseLayout from '@zego/layouts/BaseLayout';
import withData from '@zego/lib/withData';

import BrokerContactModal from '@zego/components/fleet/BrokerContactModal';
import MoreInformation from '@zego/components/molecules/MoreInformation';
import ZegoIsTrusted from '@zego/components/molecules/ZegoIsTrusted';
import FleetBrokerGetAQuoteStickyBanner from '@zego/components/organisms/FleetBrokerGetAQuoteStickyBanner';
import Hero from '@zego/components/organisms/FleetBrokerHero';
import BrokerTestimonials from '@zego/components/organisms/FleetBrokerTestimonials';
import BrokerWhatsIncluded from '@zego/components/organisms/FleetBrokerWhatsIncluded';
import BrokerWhichPolicySection from '@zego/components/organisms/FleetBrokerWhichPolicySection';
import BrokerWhoDoWeInsure from '@zego/components/organisms/FleetBrokerWhoDoWeInsure';
import BrokerWhyChooseZego from '@zego/components/organisms/FleetBrokerWhyChooseZego';

interface BrokerLandingPageProps {
  viewer: brokerLandingPageQueryResponse['viewer'];
}

const BrokerLandingPage = ({
  viewer,
}: BrokerLandingPageProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <BaseLayout
        viewer={viewer}
        title="Brokers | Zego"
        description="Why pay for insurance when you don’t use it? Zego offers flexible insurance cover for food and courier delivery drivers when they are working. Save money with Zego."
        structuredData={{
          '@context': 'https://schema.org/',
          '@type': 'Product',
          name: 'Fleet insurance',
          image:
            'https://static.zego.com/_next/static/images/Logo-d12c58c0bd8be86c8f9a2821c735258f.svg',
          description:
            'Flexible insurance for courier and private hire fleets',
          brand: 'Zego',
        }}
        headerIsInitiallyLightOnDark={false}
        upshiftContent={true}
      >
        <Hero getQuoteTextOnClick={(): void => setIsModalOpen(true)} />
        <section id="section-with-sticky-banner" />
        <BrokerWhyChooseZego />
        <BrokerWhatsIncluded />
        <BrokerWhoDoWeInsure />
        <ZegoIsTrusted />
        <BrokerWhichPolicySection />
        <BrokerTestimonials />
        <MoreInformation />
        <FleetBrokerGetAQuoteStickyBanner
          onClickAction={(): void => setIsModalOpen(true)}
          elementIdToShowOn="section-with-sticky-banner"
        />
      </BaseLayout>
      {isModalOpen && (
        <BrokerContactModal
          isOpen={isModalOpen}
          onRequestClose={(): void => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default withData(BrokerLandingPage, {
  query: graphql`
    query brokerLandingPageQuery($country: String!) {
      viewer {
        ...BaseLayout_viewer @arguments(country: $country)
      }
    }
  `,
  getVariables: ({ query }) => {
    return {
      country: query.country,
    };
  },
});
